import React, { useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import VerifiedSubmitButton from './VerifiedSubmitButton';

const SaveDropdownButton = ({
  onSave,
  onSaveAndEmail,
  disabled = false,
  isLoading = false,
  requiresVerification = false,
  verificationMessage,
  className = '',
}) => {
  const [currentAction, setCurrentAction] = useState('save');

  const sharedButtonClasses =
    'inline-flex items-center bg-accent-500 text-cream-50 text-sm font-medium tracking-[-0.005em] ' +
    'hover:bg-accent-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 ' +
    'transition-[background-color,transform] duration-150 ease-editorial';

  const baseButtonClasses = `${sharedButtonClasses} rounded-l-pill px-4 py-2 ${
    disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.98]'
  }`;

  const dropdownButtonClasses = `${sharedButtonClasses} rounded-r-pill border-l border-l-white/20 px-2 py-2 ${
    disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.98]'
  }`;

  const handleSave = () => {
    setCurrentAction('save');
    onSave();
  };

  const handleSaveAndEmail = () => {
    setCurrentAction('saveAndEmail');
    onSaveAndEmail();
  };

  const getButtonText = () => {
    if (isLoading) {
      return currentAction === 'saveAndEmail' ? 'Saving & emailing…' : 'Saving…';
    }
    return 'Save';
  };

  const SaveButton = ({ onClick, text }) => {
    if (requiresVerification) {
      return (
        <VerifiedSubmitButton
          type="button"
          disabled={isLoading || disabled}
          className={baseButtonClasses}
          onClick={onClick}
          verificationMessage={verificationMessage}
        >
          {text}
        </VerifiedSubmitButton>
      );
    } else {
      return (
        <button
          type="button"
          disabled={isLoading || disabled}
          className={baseButtonClasses}
          onClick={onClick}
        >
          {text}
        </button>
      );
    }
  };

  return (
    <div className={`relative inline-flex rounded-pill shadow-hairline ${className}`}>
      <SaveButton onClick={handleSave} text={getButtonText()} />

      <Menu as="div" className="block">
        <MenuButton
          disabled={isLoading || disabled}
          className={dropdownButtonClasses}
        >
          <span className="sr-only">Open save options</span>
          <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
        </MenuButton>

        <MenuItems
          anchor="bottom end"
          transition
          className="z-[70] w-56 origin-top-right rounded-card bg-white py-1 shadow-lift focus:outline-none [--anchor-gap:theme(spacing.2)] transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          <MenuItem>
            {({ focus, close }) => (
              <button
                type="button"
                onClick={() => {
                  handleSave();
                  close();
                }}
                className={`${
                  focus ? 'bg-cream-200' : ''
                } block w-full text-left px-4 py-2 text-sm text-ink-700`}
              >
                Save Changes Only
              </button>
            )}
          </MenuItem>
          <MenuItem>
            {({ focus, close }) => (
              <button
                type="button"
                onClick={() => {
                  handleSaveAndEmail();
                  close();
                }}
                className={`${
                  focus ? 'bg-cream-200' : ''
                } block w-full text-left px-4 py-2 text-sm text-ink-700`}
              >
                Save & Email Client
                <span className="block text-xs text-ink-500 mt-1">
                  Notify client of changes
                </span>
              </button>
            )}
          </MenuItem>
        </MenuItems>
      </Menu>
    </div>
  );
};

export default SaveDropdownButton;
